import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import { Form, Table } from "react-bootstrap";
import Button from "../../../components/atoms/button/Button";
import { toast } from "react-toastify";
import { getProductTypes, getDashboardData } from "./AnalystDashboardApi";
import "./AnalystDashboard.css";
import SecondTab from "../../../components/organisms/secondTab";

/* --------------------------------------------------
    Helper Functions
----------------------------------------------------- */
function generateColors(count) {
  const palette = [
    "#53B5F0", "#FC5F82", "#E8CB49", "#8288FA", "#61E083",
    "#FFA14F", "#59E0DF", "#838383", "#7766CC", "#77CC77",
    "#4477AA", "#AA4466", "#229966", "#664422", "#442266",
  ];
  return Array.from({ length: count }, (_, i) => palette[i % palette.length]);
}

/* --------------------------------------------------
    Lab Trend Chart
----------------------------------------------------- */
function LabTrendChart({ data = [], productKeys = [], productColors = [] }) {
  const categories = data.map((item) => {
    if (!item?.date) return "";
    let d = item.date;

    if (d.includes("-")) {
      const [y, m, dd] = d.split("-");
      return `${m}/${dd}/${y.slice(-2)}`;
    }

    const parts = d.split("/");
    return `${parts[0]}/${parts[1]}/${parts[2].slice(-2)}`;
  });

  const series = productKeys.map((type, index) => ({
    name: type,
    data: data.map(
      (item) =>
        item?.productTypeCounts?.[type] ??
        item?.productCounts?.[type] ??
        0
    ),
    color: productColors[index],
  }));

  return (
    <div className="analyst-dashboard-custom-card analyst-dashboard-lab-trend-chart">
      <div className="analyst-dashboard-chart-header analyst-dashboard-center">
        <h6 className="analyst-dashboard-chart-title">Lab Product Trend</h6>
      </div>

      <ReactApexChart
        options={{
          chart: { stacked: true, toolbar: { show: false }, foreColor: "#495057" },
          dataLabels: {
            enabled: true,
            formatter: (val, opts) => {
              if (opts.seriesIndex === opts.w.config.series.length - 1) {
                const total = opts.w.globals.stackedSeriesTotals[opts.dataPointIndex];
                return total > 0 ? total : "";
              }
              return "";
            },
            style: { fontSize: "12px", fontWeight: "bold" },
          },
          xaxis: { categories, labels: { rotate: -45, style: { fontSize: "12px" } } },
          colors: productColors,
          grid: { yaxis: { lines: { show: true } } },
        }}
        series={series}
        type="bar"
        height={300}
      />
    </div>
  );
}

/* --------------------------------------------------
    Species Trend Chart
----------------------------------------------------- */
function LabSpeciesTrendChart({ data = [] }) {
  const categories = data.map((item) => item.species || "Unknown");
  const series = [{ name: "Total Species Drug", data: data.map((i) => i.sampleCount || 0) }];
  const colors = generateColors(categories.length);

  return (
    <div className="analyst-dashboard-custom-card analyst-dashboard-lab-species-trend-chart">
      <div className="analyst-dashboard-chart-header analyst-dashboard-center">
        <h6 className="analyst-dashboard-chart-title">Lab Species Trend</h6>
      </div>

      <ReactApexChart
        options={{
          chart: { type: "bar", toolbar: { show: false } },
          dataLabels: {
            enabled: true,
            formatter: (v) => (v > 0 ? v : ""),
            style: { fontSize: "12px", fontWeight: "bold" },
          },
          xaxis: { categories },
          colors,
        }}
        series={series}
        type="bar"
        height={300}
      />
    </div>
  );
}

/* --------------------------------------------------
    Peer Review Table
----------------------------------------------------- */
function FinalReviewTable({ data = [] }) {
  const navigate = useNavigate();

  const columns = [
    "SampleID", "Product", "Company", "Date", "Unit", "ExpLevel",
    "LowLimit", "UpLimit", "Assay", "Assayby", "Re-Assay1",
    "Re-Assay1by", "Re-Assay2", "Re-Assay2by", "Result",
  ];

  const nameMap = {
    SampleID: "Sample ID",
    ExpLevel: "Exp Level",
    LowLimit: "Low Limit",
    UpLimit: "Up Limit",
    Assayby: "Assay by",
    "Re-Assay1by": "Re-Assay1 by",
    "Re-Assay2by": "Re-Assay2 by",
  };

  return (
    <div className="analyst-dashboard-peer-review-container">
      <div className="analyst-dashboard-peer-review-header">
        <h6 className="analyst-dashboard-header-default-style">Peer Review</h6>
        <Button variant="outlined" size="sm" onClick={() => navigate("/analyst-tasks/peer-review")}>
          View All
        </Button>
      </div>

      <div className="analyst-dashboard-peer-review-table-wrapper">
        <Table striped bordered hover size="sm" className="analyst-dashboard-peer-review-table table analyst-dashboard-table-borderless">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col}>{nameMap[col] || col}</th>
              ))}
            </tr>
          </thead>

          <tbody>
            {data.slice(0, 5).map((row, i) => (
              <tr key={i}>
                {columns.map((c) => (
                  <td key={c}>{row[c] || ""}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
}

/* --------------------------------------------------
    MAIN DASHBOARD
----------------------------------------------------- */
const AnalystDashboard = ({ TabConfig }) => {
  const navigate = useNavigate();

  const [selectedLocationId, setSelectedLocationId] = useState(
    Number(localStorage.getItem("selectedLocationId")) || null
  );
  const [selectedLocation, setSelectedLocation] = useState(
    localStorage.getItem("selectedLocationName") || ""
  );

  const [userName, setUserName] = useState("");
  const [loading, setLoading] = useState(false);
  const [productTypes, setProductTypes] = useState([]);

  const [dashboardData, setDashboardData] = useState({
    labProductTrend: { labTrends: [] },
    labSpeciesTrend: [],
    peerReviewData: [],
  });

  /* Default Filter Dates */
  const [filters, setFilters] = useState(() => {
    const today = new Date();
    const oneMonthAgo = new Date(today);
    oneMonthAgo.setMonth(today.getMonth() - 1);

    return {
      productType: "",
      fromDate: oneMonthAgo.toISOString().split("T")[0],
      toDate: today.toISOString().split("T")[0],
    };
  });

  /* --------------------------------------------------
      Load + STORE User + Location
----------------------------------------------------- */
  useEffect(() => {
    try {
      const raw = localStorage.getItem("userData");
      if (!raw) return navigate("/");

      let user = JSON.parse(raw);

      // ⭐ Ensure correct userData stays in localStorage
      localStorage.setItem("userData", JSON.stringify(user));

      setUserName(user.name || user.fullName || user.username || "User");

      const savedId = localStorage.getItem("selectedLocationId");
      if (!savedId) {
        if (user?.locations?.length > 0) {
          const loc = user.locations[0];
          localStorage.setItem("selectedLocationId", loc.locationId);
          localStorage.setItem("selectedLocationName", loc.locationName);

          setSelectedLocationId(loc.locationId);
          setSelectedLocation(loc.locationName);
        }
      } else {
        setSelectedLocationId(Number(savedId));
        setSelectedLocation(localStorage.getItem("selectedLocationName") || "");
      }
    } catch {
      navigate("/");
    }
  }, [navigate]);

  /* --------------------------------------------------
      Load Product Types
----------------------------------------------------- */
  useEffect(() => {
    if (!selectedLocationId) return;

    const loadTypes = async () => {
      try {
        const res = await getProductTypes();
        if (res.success && Array.isArray(res.results)) {
          const filtered = res.results.filter(
            (pt) => pt.locationId === selectedLocationId
          );
          setProductTypes(filtered);
        }
      } catch {
        toast.error("Failed to load product types");
      }
    };

    loadTypes();
  }, [selectedLocationId]);

  /* --------------------------------------------------
      Load Dashboard Data — CORRECT API MAPPING
----------------------------------------------------- */
  useEffect(() => {
    if (!selectedLocationId) return;

    const loadDashboard = async () => {
      try {
        console.log("📌 Loading dashboard with:", {
          filters,
          selectedLocationId,
        });

        const res = await getDashboardData({
          productType: filters.productType || "",
          fromDate: filters.fromDate,
          toDate: filters.toDate,
          locationId: selectedLocationId,
        });

        console.log("✅ Dashboard API Response:", res);

        if (!res.success) {
          toast.error("Failed to load dashboard data");
          return;
        }

        // ⭐ MATCHING your REAL API structure
        const extracted = {
          labProductTrend: res.labProductTrend || { labTrends: [] },
          labSpeciesTrend: res.labSpeciesTrend || [],
          peerReviewData: res.peerReviewData || [],
        };

        console.log("📊 DashboardData being set:", extracted);

        setDashboardData(extracted);
      } catch (err) {
        console.error("🔥 Dashboard load error:", err);
        toast.error("Failed to load dashboard data");
      }
    };

    loadDashboard();
  }, [filters, selectedLocationId]);

  /* --------------------------------------------------
      Filter Handlers
----------------------------------------------------- */
  const handleFilterChange = (name, value) =>
    setFilters((prev) => ({ ...prev, [name]: value }));

  const handleSearch = async (e) => {
    e.preventDefault();

    try {
      const res = await getDashboardData({
        productType: filters.productType || "",
        fromDate: filters.fromDate,
        toDate: filters.toDate,
        locationId: selectedLocationId,
      });

      if (!res.success) {
        toast.error("Failed to load dashboard data");
        return;
      }

      const extracted = {
        labProductTrend: res.labProductTrend || { labTrends: [] },
        labSpeciesTrend: res.labSpeciesTrend || [],
        peerReviewData: res.peerReviewData || [],
      };

      setDashboardData(extracted);
    } catch {
      toast.error("Failed to load dashboard data");
    }
  };

  /* --------------------------------------------------
      Chart Setup
----------------------------------------------------- */
  const rawLabTrends =
    dashboardData.labProductTrend?.labTrends ||
    dashboardData.productTrends ||
    [];

  const LAB_PRODUCT_KEYS = productTypes.map((pt) => pt.name);
  const PRODUCT_COLORS = generateColors(LAB_PRODUCT_KEYS.length);

  return (
    <>
      <SecondTab tabConfig={TabConfig} />

      <div className="analyst-dashboard-bg">
        {/* Filters */}
        <div
          className="analyst-dashboard-filter-section analyst-dashboard-custom-border-radius-sm"
        >
          <Form
            className="analyst-dashboard-custom-form-flex analyst-dashboard-custom-align-items-center analyst-dashboard-custom-gap-3"
            onSubmit={handleSearch}
          >
            <div className="analyst-dashboard-custom-form-flex analyst-dashboard-custom-align-items-center">
              <Form.Label className="analyst-dashboard-custom-form-label analyst-dashboard-filter-label">Product</Form.Label>
              <Form.Select
                className="analyst-dashboard-filter-select analyst-dashboard-custom-border-radius-md"
                value={filters.productType}
                onChange={(e) => handleFilterChange("productType", e.target.value)}
              >
                <option value="">Select</option>
                {productTypes.map((pt) => (
                  <option key={pt.productTypeId} value={pt.productTypeId}>
                    {pt.name}
                  </option>
                ))}
              </Form.Select>
            </div>

            <div className="analyst-dashboard-custom-form-flex analyst-dashboard-custom-align-items-center">
              <Form.Label className="analyst-dashboard-custom-form-label analyst-dashboard-filter-label">From</Form.Label>
              <Form.Control
                type="date"
                className="analyst-dashboard-date-input analyst-dashboard-custom-border-radius-md"
                value={filters.fromDate}
                onChange={(e) => handleFilterChange("fromDate", e.target.value)}
              />
            </div>

            <div className="analyst-dashboard-custom-form-flex analyst-dashboard-custom-align-items-center">
              <Form.Label className="analyst-dashboard-custom-form-label analyst-dashboard-filter-label">To</Form.Label>
              <Form.Control
                type="date"
                className="analyst-dashboard-date-input"
                value={filters.toDate}
                onChange={(e) => handleFilterChange("toDate", e.target.value)}
              />
            </div>

            <Button
              className="btn-common outlined-active-btn analyst-dashboard-btn-md-size default-font-style"
              type="submit"
            >
              Search
            </Button>
          </Form>
        </div>

        {/* Charts Row */}
        <div className="analyst-dashboard-charts-custom-row analyst-dashboard-custom-margin-bottom-3">
          <div className="analyst-dashboard-charts-col-lg-8 analyst-dashboard-custom-margin-bottom-3 analyst-dashboard-custom-margin-bottom-lg-0">
            <LabTrendChart
              data={rawLabTrends}
              productKeys={LAB_PRODUCT_KEYS}
              productColors={PRODUCT_COLORS}
            />
          </div>

          <div className="analyst-dashboard-charts-col-lg-4">
            <LabSpeciesTrendChart data={dashboardData.labSpeciesTrend || []} />
          </div>
        </div>

        {/* Peer Review */}
        <div className="analyst-dashboard-peer-review-row">
          <div className="analyst-dashboard-peer-review-col-12">
            <div className="analyst-dashboard-custom-card analyst-dashboard-peer-review-section">
              <FinalReviewTable data={dashboardData.peerReviewData || []} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AnalystDashboard;
